// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.

import * as BABYLON from 'babylonjs';
import * as Interfaces from '../../Interfaces/Interfaces';
import * as Data from '../Data/WebData';
import * as Enumerations from '../../Enumerations/Enumerations';
import {
    applyAdvancedMaterialTuningToMaterial,
    createTriplanarPbrMaterial,
    getAdvancedTextureMaterialId,
    isAdvancedTextureMaterialForPreset
} from '../Materials/AdvancedTriplanarPbrMaterial';
import {
    ADVANCED_MATERIAL_PRESETS,
    getAdvancedMaterialTuning,
    getAdvancedMaterialPreset,
    isAdvancedMaterialParallaxEnabled
} from '../Materials/AdvancedMaterialPresets';
class MaterialManager implements Interfaces.Interfaces.IMaterialsManager {

    // ------- Mesh-occlusion (depth-map) toggle -------
    private _pcMeshOcclusionEnabled: boolean = false;
    private _pcMeshOcclusionTolM: number = 0.01; // meters (1cm default)

    private _pcDepthIsPacked: boolean = false;
    private _pointMaterialWarmed = false;
    private _depthRenderer: BABYLON.DepthRenderer | null = null;
    private _meshDepthTex: BABYLON.RenderTargetTexture | null = null;
    private _depthCamera: BABYLON.Camera | null = null;

    private _covNudge: number = 0;
    // ------- Point cloud knobs -------
    private _pcNearMinPx: number = 7.0;
    private _pcCoverageScale: number = 1.3; // current *default*
    private _covDefault: number = 1.3;      // same as above; fallback for unmapped voxels
    private _covByVoxel = new Map<number, number>(); // per-voxel coverageScale overrides

    // Caches of SPLAT materials keyed by voxel size (mm)
    private _splatByMM: Map<number, BABYLON.ShaderMaterial> = new Map();
    private _splatSet: Set<BABYLON.ShaderMaterial> = new Set();

    // Dynamic size envelope (applied to all SPLAT mats)
    private _pcDynBase: { minPx: number; maxPx: number; farPx: number } = { minPx: 0.1, maxPx: 20.0, farPx: 0.3 };
    private _pcDynScale: number = 1.0;

    private _pcOrigPointSize: number = 0.1; // px (for ORIG mode)

    // Back-compat holders
    private _pointCloudShaderMaterial: BABYLON.Material | null = null;
    private _pointCloudSplatShaderMaterial: BABYLON.ShaderMaterial | null = null; // default 5mm
    private _pointCloudShaderMaterialOrig: BABYLON.ShaderMaterial;
    private _pointCloudMaterial: any;

    private _pointMode: 'splat' | 'orig' = 'splat';
    private _defaultSplatMM: number = 5;

    // ------- Scalar materials (as in your code) -------
    private _materials: { [id: string]: BABYLON.StandardMaterial; } = {};
    private _markedPointMaterials: { [id: string]: MarkedPointMaterial; } = {};
    private _materialKeys: string[] = [];
    private _defaultMaterial: BABYLON.StandardMaterial;
    private _colorVertexMaterial: BABYLON.StandardMaterial;
    private _colorVertexBackfaceMaterial: BABYLON.StandardMaterial;
    private _xrayMaterial: BABYLON.StandardMaterial;
    private _junctionMaterial: BABYLON.StandardMaterial;
    private _waypointMaterial: BABYLON.StandardMaterial;
    private _pathMaterial: BABYLON.StandardMaterial;
    private _measurementMaterial: MarkedPointMaterial;
    private _defaultMPMaterial: MarkedPointMaterial;
    private _scanMaterial: BABYLON.StandardMaterial;
    private _hiddenSelectionMaterial: BABYLON.StandardMaterial;
    private _regionBoxMaterial: BABYLON.StandardMaterial;
    private _regionPointMaterial: BABYLON.StandardMaterial;
    private _regionMeshMaterial: BABYLON.StandardMaterial;
    private _regionAllMaterial: BABYLON.StandardMaterial;
    private _advancedTextureMaterials: { [presetId: string]: BABYLON.NodeMaterial } = {};
    private _advancedMaterialTunings: Record<string, IAdvancedMaterialTuning> = {};

    private _scene: BABYLON.Scene;
    private _displayMode: Enumerations.MaterialEdgeMode = Enumerations.MaterialEdgeMode.SOLID;
    private _isSelectionMaterialOverriden: boolean;

    private static _shadersRegistered = false;

    private static SELECTION_MATERIAL_OPAQUE = "SelMatOpaque";
    private static SELECTION_MATERIAL_TRANSPARENT = "SelMatTransp";
    private static HIDDEN_MATERIAL = "HidMat";

    private static DEF_SELECTION_COLOR = new BABYLON.Color3(.0, .8, .8);
    private static DEF_SELECTION_EMISSIVE_COLOR = new BABYLON.Color3(.0, .2, .2);

    private _hexMaterials: { [key: string]: BABYLON.StandardMaterial } = {};

    constructor(scene: BABYLON.Scene) {
        this._scene = scene;

        // ---------- Scalar materials ----------
        const mat = new BABYLON.StandardMaterial("DefaultMaterial", this._scene);
        mat.diffuseColor = new BABYLON.Color3(.5, .5, .5);
        mat.ambientColor = new BABYLON.Color3(.5, .5, .5);
        mat.specularColor = new BABYLON.Color3(.5, .5, .5);
        mat.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        mat.specularPower = 64;
        mat.id = "DefaultMaterial";

        const pvc = new BABYLON.StandardMaterial("VertexColorMaterial", this._scene);
        pvc.wireframe = false;
        pvc.ambientColor = new BABYLON.Color3(1, 1, 1);
        pvc.specularColor = new BABYLON.Color3(0, 0, 0);
        pvc.emissiveColor = new BABYLON.Color3(0, 0, 0);

        const pvcb = new BABYLON.StandardMaterial("VertexColorBackfaceMaterial", this._scene);
        pvcb.wireframe = false;
        pvcb.ambientColor = new BABYLON.Color3(1, 1, 1);
        pvcb.specularColor = new BABYLON.Color3(0, 0, 0);
        pvcb.emissiveColor = new BABYLON.Color3(0, 0, 0);
        pvcb.backFaceCulling = false;

        const xrayMat = new BABYLON.StandardMaterial("XRayMaterial", this._scene);
        xrayMat.diffuseColor = BABYLON.Color3.Gray();
        xrayMat.specularColor = new BABYLON.Color3(0, 0, 0);
        xrayMat.ambientColor = new BABYLON.Color3(0, 0, 0);
        xrayMat.emissiveColor = new BABYLON.Color3(0, 0, 0);
        xrayMat.specularPower = 64;
        xrayMat.alpha = .3;
        xrayMat.id = "XRayMaterial";

        const juncMat = new BABYLON.StandardMaterial("JunctionMaterial", this._scene);
        juncMat.diffuseColor = BABYLON.Color3.White();
        juncMat.specularColor = new BABYLON.Color3(.5, .5, .5);
        juncMat.ambientColor = new BABYLON.Color3(.5, .5, .5);
        juncMat.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        juncMat.specularPower = 64;
        juncMat.id = "JunctionMaterial";

        const wayMat = new BABYLON.StandardMaterial("WaypointMaterial", this._scene);
        wayMat.diffuseColor = BABYLON.Color3.Teal();
        wayMat.specularColor = new BABYLON.Color3(.5, .5, .5);
        wayMat.ambientColor = new BABYLON.Color3(.5, .5, .5);
        wayMat.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        wayMat.specularPower = 64;
        wayMat.id = "WaypointMaterial";

        const pathMat = new BABYLON.StandardMaterial("PathMaterial", this._scene);
        pathMat.diffuseColor = BABYLON.Color3.Gray();
        pathMat.specularColor = new BABYLON.Color3(.5, .5, .5);
        pathMat.ambientColor = new BABYLON.Color3(.5, .5, .5);
        pathMat.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        pathMat.specularPower = 14;
        pathMat.alpha = .5;
        pathMat.id = "PathMaterial";

        // ---------- Shaders registration (once) ----------
        this._registerPointShadersIfNeeded();

        // ---------- SPLAT default (5mm) ----------
        const defaultSplat = this._createSplatMaterial(this._defaultSplatMM / 1000);
        this._splatByMM.set(this._defaultSplatMM, defaultSplat);
        this._splatSet.add(defaultSplat);
        this._pointCloudSplatShaderMaterial = defaultSplat;
        this._pointCloudShaderMaterial = defaultSplat;

        // ---------- ORIGINAL fixed-size points ----------
        const pointMaterialOrig = new BABYLON.ShaderMaterial(
            "pointCloudShader_orig",
            scene,
            { vertex: "pointOrig", fragment: "pointOrig" },
            {
                attributes: ["position", "color", "normal"],
                uniforms: [
                    "world", "worldViewProjection", "uPointSize",
                    // clip uniforms:
                    "uClipPlaneCount",
                    "uClipPlane1", "uClipPlane2", "uClipPlane3",
                    "uClipPlane4", "uClipPlane5", "uClipPlane6"
                ],
            }
        );
        pointMaterialOrig.backFaceCulling = false;
        pointMaterialOrig.pointsCloud = true;
        pointMaterialOrig.alphaMode = BABYLON.Constants.ALPHA_DISABLE;
        pointMaterialOrig.disableDepthWrite = false;
        pointMaterialOrig.setFloat("uPointSize", this._pcOrigPointSize);
        this._pointCloudShaderMaterialOrig = pointMaterialOrig;

        this._pointCloudShaderMaterialOrig.onBindObservable.add(() => {
            this._bindClipPlanesTo(this._pointCloudShaderMaterialOrig);
        });

        // ---------- Simple Standard points (legacy) ----------
        const pointMat = new BABYLON.StandardMaterial("PointCloudMaterial", scene);
        pointMat.pointsCloud = true;
        pointMat.pointSize = .5;
        pointMat.emissiveColor = BABYLON.Color3.White();
        pointMat.diffuseColor = BABYLON.Color3.White();
        pointMat.alpha = 0.3;
        pointMat.transparencyMode = BABYLON.Material.MATERIAL_ALPHABLEND;
        pointMat.needDepthPrePass = true;
        pointMat.disableLighting = true;
        pointMat.allowShaderHotSwapping = true;

        // This is another attempt and safeguard for initializing the alpha point material so that the shader is ready to go.
        // if the shader is not initliazed, then its first usage causes a flicker, or a brief blackness for the points. Note, this
        // dummy usage might be unneccessary since the "warm" is doing the same thing, but overkill of warmth for the alpha shader
        // shouldn't hurt.
        var d1 = this.makePointDummy(this._scene, true);
        var d2 = this.makePointDummy(this._scene, false);

        pointMat.forceCompilation(d1, () => {
            d1.dispose();
        });
        pointMat.forceCompilation(d2, () => {
            d2.dispose();
        });

        this._pointCloudMaterial = pointMat;

        scene.clearColor = new BABYLON.Color4(0, 0, 0, 1);
        scene.autoClear = true;

        // ---------- Per-frame focalPx for all splats + bind clip planes ----------
        scene.onBeforeRenderObservable.add(() => {
            const cam = scene.activeCamera as BABYLON.Camera;
            if (!cam) return;

            const eng = scene.getEngine();
            const w = eng.getRenderWidth(true);
            const h = eng.getRenderHeight(true);

            const useVertical = (cam.fovMode === BABYLON.Camera.FOVMODE_VERTICAL_FIXED);
            const H = useVertical ? eng.getRenderHeight(true) : eng.getRenderWidth(true);
            const focalPx = H / (2.0 * Math.tan(cam.fov * 0.5));

            if (this._pcMeshOcclusionEnabled) {
                this._ensureDepthResources();
                // If meshes change often, you can refresh occasionally (or call refreshPointCloudMeshOccluders() from loaders)
                // this._updateMeshDepthRenderList();
            }

            this._splatByMM.forEach((mat) => {
                mat.setFloat("focalPx", focalPx);
                this._bindClipPlanesTo(mat);

                mat.setVector2("screenSize", new BABYLON.Vector2(w, h));

                mat.setFloat("cameraNear", cam.minZ);
                mat.setFloat("cameraFar", cam.maxZ);
                mat.setInt("uDepthIsPacked", this._pcDepthIsPacked ? 1 : 0);
                mat.setInt("uUseMeshOcclusion", this._pcMeshOcclusionEnabled ? 1 : 0);

                if (this._pcMeshOcclusionEnabled && this._meshDepthTex) {
                    mat.setTexture("meshDepthSampler", this._meshDepthTex);
                }
            });

            if (this._pointCloudShaderMaterialOrig) {
                this._bindClipPlanesTo(this._pointCloudShaderMaterialOrig);
            }
        });


        // ---------- Apply current envelopes to existing mats ----------
        this.applyDynSizeToAllSplat();

        // ---------- Keyboard shims ----------
        const applyOrigSize = () => this._pointCloudShaderMaterialOrig.setFloat("uPointSize", this._pcOrigPointSize);

        this._scene.onKeyboardObservable.add((kb) => {
            if (kb.type !== BABYLON.KeyboardEventTypes.KEYDOWN) return;
            const key = kb.event.key.toLowerCase();

            const grow = key === "]" || key === "=";
            const shrink = key === "[" || key === "-";
            if (!grow && !shrink) return;

            if (this._pointMode === "orig") {
                const step = kb.event.shiftKey ? 1.5 : 1.15;
                this._pcOrigPointSize *= grow ? step : 1.0 / step;
                this._pcOrigPointSize = Math.min(Math.max(this._pcOrigPointSize, 1.0), 256.0);
                applyOrigSize();
                console.log(`[PointCloud][ORIG] uPointSize = ${this._pcOrigPointSize.toFixed(2)} px`);
            } else {
                const step = 0.1;
                this._covNudge += (grow ? step : -step);
                // no need to clamp here; we clamp per-material when applying
                this.applyDynSizeToAllSplat();
                console.log(`[PointCloud][SPLAT] coverage nudge = ${this._covNudge.toFixed(2)} (effective = base + nudge)`);
            }
        });

        // ---------- Book-keeping ----------
        this._defaultMaterial = mat;
        this._colorVertexMaterial = pvc;
        this._colorVertexBackfaceMaterial = pvcb;
        this._junctionMaterial = juncMat;
        this._waypointMaterial = wayMat;
        this._pathMaterial = pathMat;
        this._xrayMaterial = xrayMat;

        this._materialKeys.push(this._defaultMaterial.id);
        this._materialKeys.push(this._xrayMaterial.id);
        this._materials[this._defaultMaterial.id] = this._defaultMaterial;
        this._materials[this._xrayMaterial.id] = this._xrayMaterial;

        // Marked point defaults
        const mpDefault = new MarkedPointMaterial();
        mpDefault.backgroundColor = "blue";
        mpDefault.captionFont = new Data.MaterialData.Font(false, "", "Times New Roman", 12, false, false, false);
        mpDefault.captionTextColor = "white";
        mpDefault.id = "MPDefault";
        mpDefault.markerAppeararnceId = "DefaultMaterial";
        mpDefault.lineColor = "red";
        mpDefault.lineWidth = 1;
        mpDefault.tagShape = Enumerations.AnnotationLabelShape.RECTANGLE;
        mpDefault.primShape = Enumerations.MarkedPointPrimitiveShape.SPHERE;
        mpDefault.markerSize = 30;
        mpDefault.tagVisible = true;

        const scanMat = new BABYLON.StandardMaterial("ScanMaterial", this._scene);
        scanMat.diffuseColor = new BABYLON.Color3(0, .498, 0);
        scanMat.ambientColor = new BABYLON.Color3(0, .498, 0);
        scanMat.specularColor = new BABYLON.Color3(0, 0, 0);
        scanMat.emissiveColor = new BABYLON.Color3(0, 0, 0);
        scanMat.specularPower = 14;
        scanMat.alpha = 1;
        scanMat.id = "ScanMaterial";

        const measurementMaterial = new MarkedPointMaterial();
        measurementMaterial.backgroundColor = "white";
        measurementMaterial.captionFont = new Data.MaterialData.Font(true, "", "Times New Roman", 12, false, false, false);
        measurementMaterial.captionTextColor = "black";
        measurementMaterial.id = "MPDefault";
        measurementMaterial.markerAppeararnceId = "MeasurementMaterial";
        measurementMaterial.lineColor = "#FCDC3B";
        measurementMaterial.lineWidth = 1;
        measurementMaterial.tagShape = Enumerations.AnnotationLabelShape.CIRCLE;
        measurementMaterial.primShape = Enumerations.MarkedPointPrimitiveShape.SPHERE;
        measurementMaterial.markerSize = 30;
        measurementMaterial.tagVisible = true;

        const measurementMarkerMaterial = new BABYLON.StandardMaterial("MeasurementMaterial", this._scene);
        measurementMarkerMaterial.diffuseColor = new BABYLON.Color3(.5, .5, .5);
        measurementMarkerMaterial.ambientColor = new BABYLON.Color3(.5, .5, .5);
        measurementMarkerMaterial.specularColor = new BABYLON.Color3(.5, .5, .5);
        measurementMarkerMaterial.emissiveColor = new BABYLON.Color3(.5, .5, .5);
        measurementMarkerMaterial.specularPower = 64;
        measurementMarkerMaterial.id = "MeasurementMaterial";

        const hiddenSelMat = new BABYLON.StandardMaterial("HiddenSelectionMat", this._scene);
        hiddenSelMat.diffuseColor = new BABYLON.Color3(1, 1, 1);
        hiddenSelMat.specularPower = 64;
        hiddenSelMat.alpha = .2;
        hiddenSelMat.id = "HiddenSelectionMat";

        const regionBoxMaterial = new BABYLON.StandardMaterial("RegionBoxMat", this._scene);
        regionBoxMaterial.diffuseColor = new BABYLON.Color3(1, 0, 0);
        regionBoxMaterial.specularPower = 64;
        regionBoxMaterial.alpha = .15;
        regionBoxMaterial.id = "RegionBoxMat";

        const regionPointMaterial = new BABYLON.StandardMaterial("RegionPointMat", this._scene);
        regionPointMaterial.diffuseColor = new BABYLON.Color3(0, 0, 1);
        regionPointMaterial.specularPower = 64;
        regionPointMaterial.alpha = .15;
        regionPointMaterial.id = "RegionPointMat";

        const regionMeshMaterial = new BABYLON.StandardMaterial("RegionMeshMat", this._scene);
        regionMeshMaterial.diffuseColor = new BABYLON.Color3(0, 1, 0);
        regionMeshMaterial.specularPower = 64;
        regionMeshMaterial.alpha = .15;
        regionMeshMaterial.id = "RegionBoxMat";

        const regionAllMaterial = new BABYLON.StandardMaterial("RegionAllMat", this._scene);
        regionAllMaterial.diffuseColor = new BABYLON.Color3(.5, .5, 0);
        regionAllMaterial.specularPower = 64;
        regionAllMaterial.alpha = .25;
        regionAllMaterial.id = "RegionBoxMat";

        this._regionBoxMaterial = regionBoxMaterial;
        this._regionPointMaterial = regionPointMaterial;
        this._regionMeshMaterial = regionMeshMaterial;
        this._regionAllMaterial = regionAllMaterial;

        this._materialKeys.push(measurementMarkerMaterial.id);
        this._materials[measurementMarkerMaterial.id] = measurementMarkerMaterial;

        this._materialKeys.push(hiddenSelMat.id);
        this._materials[hiddenSelMat.id] = hiddenSelMat;

        this._measurementMaterial = measurementMaterial;
        this._markedPointMaterials[mpDefault.id] = mpDefault;
        this._defaultMPMaterial = mpDefault;
        this._scanMaterial = scanMat;
        this._hiddenSelectionMaterial = hiddenSelMat;
        this._isSelectionMaterialOverriden = false;
    }

    // Initialize point cloud material shader logic (so that usage of material is "ready to go", and doesn't cause flicker)
    warmPointMaterial(mesh: BABYLON.Mesh) {
        if (!this._pointMaterialWarmed) {
            this._pointMaterialWarmed = true;
            this._pointCloudMaterial.forceCompilationAsync(mesh);
        }
    }
    makePointDummy(scene: BABYLON.Scene, useByteColors: boolean) {
        const dummy = new BABYLON.Mesh("__pcWarmup__", scene);
        dummy.isVisible = false;
        dummy.isPickable = false;
        dummy.isUnIndexed = true;

        // Position
        dummy.setVerticesData(BABYLON.VertexBuffer.PositionKind, [0, 0, 0], false);

        if (useByteColors) {
            const colorsU8 = new Uint8Array([255, 255, 255, 255]);
            const vb = new BABYLON.VertexBuffer(
                scene.getEngine(),
                colorsU8,
                BABYLON.VertexBuffer.ColorKind,
                false,
                false,
                0,
                false,
                0,
                4,
                BABYLON.VertexBuffer.UNSIGNED_BYTE,
                true,
                true
            );
            dummy.setVerticesBuffer(vb);
            dummy.useVertexColors = true;
        } else {
            dummy.setVerticesData(BABYLON.VertexBuffer.ColorKind, [1, 1, 1, 1], false);
            dummy.useVertexColors = true;
        }

        return dummy;
    }

    // ---------- Per-voxel coverageScale mapping ----------
    setSplatCoverageScaleByVoxel(map: Record<number | string, number>, fallback?: number) {
        if (typeof fallback === "number") {
            this._covDefault = fallback;
            this._pcCoverageScale = fallback; // keep the knob consistent
        }
        this._covByVoxel.clear();
        for (const k in (map || {})) {
            const key = Math.round(Number(k));
            const v = Number((map as any)[k]);
            if (Number.isFinite(key) && Number.isFinite(v)) this._covByVoxel.set(key, v);
        }
        // push updates to already-created materials
        this._splatByMM.forEach((mat, mm) => {
            mat.setFloat("coverageScale", this.getCoverageScaleForVoxel(mm));
        });
    }

    getCoverageScaleForVoxel(mm: number): number {
        const key = Math.round(mm);
        return this._covByVoxel.get(key) ?? this._covDefault;
    }

    // Keep old API: set a single global default
    setSplatCoverageScale(scale: number) {
        this._pcCoverageScale = Math.max(0.01, Number(scale) || 1.3);
        this._covDefault = this._pcCoverageScale;
        this.applyDynSizeToAllSplat();
    }

    private _applyDepthPackedFlagToAllSplat() {
        const v = this._pcDepthIsPacked ? 1 : 0;
        this._splatByMM.forEach((mat) => mat.setInt("uDepthIsPacked", v));
    }

    // Apply size envelope + per-voxel coverage to all SPLAT materials
    applyDynSizeToAllSplat = () => {
        const minPx = this._pcDynBase.minPx * this._pcDynScale;
        const maxPx = this._pcDynBase.maxPx * this._pcDynScale;
        const farPx = this._pcDynBase.farPx * this._pcDynScale;

        this._splatByMM.forEach((mat, mm) => {
            mat.setFloat("minPx", minPx);
            mat.setFloat("maxPx", maxPx);
            mat.setFloat("farPx", farPx);
            mat.setFloat("nearMinPx", this._pcNearMinPx);
            const base = this.getCoverageScaleForVoxel(mm);
            mat.setFloat("coverageScale", this._clampCoverage(base + this._covNudge));
        });
    };

    // ---------- Point cloud API ----------
    /** Back-compat: default 5mm SPLAT (or ORIG mode material). */
    getPointCloudShaderMaterial(): BABYLON.Material {
        if (this._pointMode === 'orig') return this._pointCloudShaderMaterialOrig;
        let m = this._splatByMM.get(this._defaultSplatMM);
        if (!m) {
            m = this._createSplatMaterial(this._defaultSplatMM / 1000);
            this._splatByMM.set(this._defaultSplatMM, m);
            this._splatSet.add(m);
        }
        this._pointCloudSplatShaderMaterial = m;
        return m;
    }

    /** Prefer this from loaders: mm = voxel spacing in millimeters (5, 10, 25, 50, …) */
    getPointCloudSplatMaterialForResolutionMM(mm: number): BABYLON.ShaderMaterial {
        const key = Math.round(mm);
        let mat = this._splatByMM.get(key);
        if (!mat) {
            mat = this._createSplatMaterial(key / 1000);
            this._splatByMM.set(key, mat);
            this._splatSet.add(mat);
        }
        const base = this.getCoverageScaleForVoxel(key);
        mat.setFloat("coverageScale", this._clampCoverage(base + this._covNudge));
        return mat;
    }

    /** Same as above but pass spacing in meters. */
    getPointCloudSplatMaterialForSpacing(meters: number): BABYLON.ShaderMaterial {
        const mm = Math.round(meters * 1000);
        return this.getPointCloudSplatMaterialForResolutionMM(mm);
    }

    /** Assign the correct per-voxel material to a mesh. */
    assignPointCloudMaterial(mesh: BABYLON.AbstractMesh, voxelMM: number) {
        const mm = Math.round(voxelMM);
        mesh.material = this.getPointCloudSplatMaterialForResolutionMM(mm);
        mesh.metadata = mesh.metadata || {};
        mesh.metadata.voxelMM = mm;
        (mesh.metadata as any).spacingWorld = mm / 1000.0; // helps mode switcher
    }

    /** Switch all point cloud meshes in the scene between SPLAT and ORIG. */
    setPointCloudMode(mode: 'splat' | 'orig') {
        if (mode === this._pointMode) return;
        this._pointMode = mode;

        for (const m of this._scene.meshes) {
            if (!m || !m.material) continue;

            const isOrig = m.material === this._pointCloudShaderMaterialOrig;
            const isSplat = this._splatSet.has(m.material as BABYLON.ShaderMaterial);
            if (!isOrig && !isSplat) continue;

            if (mode === 'orig') {
                m.material = this._pointCloudShaderMaterialOrig;
            } else {
                const sw: number | undefined = (m as any)?.metadata?.spacingWorld;
                const mat = (sw != null)
                    ? this.getPointCloudSplatMaterialForSpacing(sw)
                    : this.getPointCloudSplatMaterialForResolutionMM(this._defaultSplatMM);
                m.material = mat;
            }
        }
    }

    getPointCloudMaterial() {
        return this._pointCloudMaterial;
    }

    // ---------- Mesh/CAD API (unchanged from your version) ----------
    setMaterialEdgeMode(mode: Enumerations.MaterialEdgeMode) {
        const wireframe = mode == Enumerations.MaterialEdgeMode.WIREFRAME;
        this._displayMode = mode;
        for (let i = 0; i < this._materialKeys.length; i++) {
            const mat = this._materials[this._materialKeys[i]];
            if (mat) mat.wireframe = wireframe;
        }
    }

    getColorVertexMaterial(): BABYLON.StandardMaterial { return this._colorVertexMaterial; }

    getColorVertexBackfaceMaterial(): BABYLON.StandardMaterial { return this._colorVertexBackfaceMaterial; }

    getDefaultMaterial(alpha: number, wireframe: boolean): BABYLON.StandardMaterial {
        return this.getOrCreateAlphaMaterial(this._defaultMaterial, alpha, wireframe);
    }

    setPointCloudMeshOcclusionEnabled(enabled: boolean) {
        enabled = !!enabled;
        if (enabled === this._pcMeshOcclusionEnabled) return;

        this._pcMeshOcclusionEnabled = enabled;

        if (enabled) {
            this._ensureDepthResources();
            this._updateMeshDepthRenderList();
        }

        this._applyMeshOcclusionBindingsToAllSplat();

        if (enabled) {

            this.setPointCloudMeshOcclusionTolerance(.20);
            this.refreshPointCloudMeshOccluders();
        }
    }

    private _ensureDepthResources() {
        const cam = this._scene.activeCamera as BABYLON.Camera;
        if (!cam) return;

        // If camera changed, re-enable depth renderer for this camera
        if (!this._depthRenderer || this._depthCamera !== cam) {
            this._depthRenderer = this._scene.enableDepthRenderer(cam, false);

            this._meshDepthTex = this._depthRenderer.getDepthMap();

            this._meshDepthTex.wrapU = BABYLON.Texture.CLAMP_ADDRESSMODE;
            this._meshDepthTex.wrapV = BABYLON.Texture.CLAMP_ADDRESSMODE;
            this._meshDepthTex.updateSamplingMode(BABYLON.Texture.NEAREST_SAMPLINGMODE);
            this._depthCamera = cam;
        }
    }

    private _updateMeshDepthRenderList() {
        if (!this._meshDepthTex) return;

        // Only render meshes that should occlude points
        this._meshDepthTex.renderList = this._scene.meshes.filter(m => {
            if (!m || !m.isEnabled() || !m.isVisible) return false;

            const mat: any = m.material;
            if (!mat) return false;

            // Exclude point clouds (your splat/orig ShaderMaterial has pointsCloud=true)
            if (mat.pointsCloud) return false;

            
            // Ignore transparent materials
            const alpha = (mat.alpha ?? 1.0);
            if (alpha < 1.0) return false;

            // If transparencyMode exists and isn't opaque, ignore
            if (mat.transparencyMode != null && mat.transparencyMode !== BABYLON.Material.MATERIAL_OPAQUE) return false;
            

            return true;
        });
    }

    private _applyMeshOcclusionBindingsToAllSplat() {
        const cam = this._scene.activeCamera as BABYLON.Camera;
        const eng = this._scene.getEngine();
        const w = eng.getRenderWidth(true);
        const h = eng.getRenderHeight(true);

        this._splatByMM.forEach((mat) => {
            mat.setInt("uUseMeshOcclusion", this._pcMeshOcclusionEnabled ? 1 : 0);
            mat.setFloat("meshCullTol", this._pcMeshOcclusionTolM);

            mat.setVector2("screenSize", new BABYLON.Vector2(w, h));
            mat.setFloat("cameraNear", cam?.minZ ?? 0.5);
            mat.setFloat("cameraFar", cam?.maxZ ?? 1000.0);
            mat.setInt("uDepthIsPacked", this._pcDepthIsPacked ? 1 : 0);
            if (this._pcMeshOcclusionEnabled) {
                this._ensureDepthResources();
                if (this._meshDepthTex) {
                    mat.setTexture("meshDepthSampler", this._meshDepthTex);
                }
            }
        });
    }


    /** Update the occluder render list from the current scene meshes (call after loading/unloading meshes). */
    refreshPointCloudMeshOccluders() {
        if (!this._pcMeshOcclusionEnabled) return;
        this._ensureDepthResources();
        this._updateMeshDepthRenderList();
    }

    /** Optional: set tolerance in meters (how close to mesh depth counts as "near"). */
    setPointCloudMeshOcclusionTolerance(meters: number) {
        const v = Number(meters);
        this._pcMeshOcclusionTolM = Number.isFinite(v) ? Math.max(0.0, v) : this._pcMeshOcclusionTolM;

        this._splatByMM.forEach(mat => mat.setFloat("meshCullTol", this._pcMeshOcclusionTolM));
    }

    getOrCreateHexMaterial(colorNumber: number): BABYLON.StandardMaterial {

        var colorHex = this._rgbaNumberToHexRRGGBBAA(colorNumber);
        if (!colorHex) {
            // Fallback to default material if something is off
            return this._defaultMaterial;
        }

        let hex = colorHex.trim();
        if (!hex.startsWith("#")) {
            hex = "#" + hex;
        }
        hex = hex.toUpperCase();

        const key = `${hex}`;
        let mat = this._hexMaterials[key];
        if (mat) {
            return mat;
        }

        const baseMat = this._defaultMaterial;

        let color: BABYLON.Color4;
        try {
            color = BABYLON.Color4.FromHexString(hex);
        } catch {
            color = baseMat.diffuseColor as any;
        }

        const name = `HexMat_${hex}_${baseMat.id}`;
        mat = new BABYLON.StandardMaterial(name, this._scene);
        // --- DIFFUSE FROM HEX ---
        mat.diffuseColor = new BABYLON.Color3(color.r, color.g, color.b);

        // --- EMISSIVE FROM DIFFUSE (with warm-color blue boost) ---
        const emissiveStrength = 0.7;      // overall glow strength
        const blueBias = 0.18;             // how much warm colors get extra blue

        // “Warmth” factor: strong for gold / yellow / orange,
        // lower for pure red/green, zero for pure blue.
        const warmFactor = (color.r + color.g) * 0.5 * (1.0 - color.b);

        mat.emissiveColor = new BABYLON.Color3(
            color.r * emissiveStrength,
            color.g * emissiveStrength,
            color.b * emissiveStrength + blueBias * warmFactor
        );

        // --- SPECULAR FROM DIFFUSE (metal-ish highlight) ---
        const specularBlend = 0.6; // 0 = same as diffuse, 1 = white highlight
        const white = BABYLON.Color3.White();

        mat.specularColor = new BABYLON.Color3(
            color.r + (white.r - color.r) * specularBlend,
            color.g + (white.g - color.g) * specularBlend,
            color.b + (white.b - color.b) * specularBlend
        );

        mat.specularPower = 200;
        mat.alpha = color.a;
        mat.wireframe = (this._displayMode == Enumerations.MaterialEdgeMode.WIREFRAME);

        mat.id = name;

        this._materials[name] = mat;
        this._materialKeys.push(name);
        this._hexMaterials[key] = mat;

        return mat;
    }

    private _numberToHexRGB(color: number): string {
        const hex = (color >>> 0).toString(16).padStart(6, "0");
        return `#${hex}`;
    }

    private _rgbaNumberToHexRRGGBBAA(color: number): string {
        const hex = (color >>> 0).toString(16).padStart(8, "0");
        return `#${hex}`;
    }

    getMaterial(materialId: string): BABYLON.StandardMaterial {
        if (!materialId) return this._defaultMaterial;
        if (materialId.startsWith("XRayMaterial")) return this._xrayMaterial;
        if (materialId.startsWith("JunctionMaterial")) return this._junctionMaterial;
        if (materialId.startsWith("WaypointMaterial")) return this._waypointMaterial;
        if (materialId.startsWith("PathMaterial")) return this._pathMaterial;
        if (materialId.startsWith("VertexColorMaterial")) return this._colorVertexMaterial;
        if (materialId.startsWith("VertexColorBackfaceMaterial")) return this._colorVertexBackfaceMaterial;
        if (materialId.startsWith("HexMat")) {
            const hexSplit = materialId.split('_');

            if (hexSplit.length == 3) {
                if (this._hexMaterials[hexSplit[1]] != null)
                    return this._hexMaterials[hexSplit[1]];
            }

            return this._defaultMaterial;
        }
        const split = materialId.split('_');
        const id = split[0];

        for (const key in this._materials) {
            if (id == (this._materials[key].id)) return this._materials[key];
        }

        const sceneMaterial = this._scene.getMaterialByID(materialId)
            || this._scene.materials.find(material => material && (material.id === materialId || material.name === materialId));

        if (sceneMaterial) {
            // Return any material type found in the scene (StandardMaterial, PBRMaterial, etc.)
            return sceneMaterial as BABYLON.StandardMaterial;
        }
        
        if (this._markedPointMaterials[materialId] != null) {
            const mpMaterialId = this._markedPointMaterials[materialId].markerAppeararnceId;
            return this.getMaterial(mpMaterialId);
        }
        if (this._markedPointMaterials[id] != null) {
            const mpMaterialId = this._markedPointMaterials[id].markerAppeararnceId;
            return this.getMaterial(mpMaterialId);
        }

        return this._defaultMaterial;
    }

    getOrCreateAdvancedTextureMaterial(materialID: string, tintColor?: number | null): string | null {
        const preset = getAdvancedMaterialPreset(materialID);
        if (!preset) return null;

        const parallaxEnabled = isAdvancedMaterialParallaxEnabled(materialID);
        const materialId = getAdvancedTextureMaterialId(preset.id, tintColor, parallaxEnabled);
        if (!this._advancedTextureMaterials[materialId]) {
            const tuning = this._advancedMaterialTunings[preset.id]
                ?? getAdvancedMaterialTuning(preset);
            const material = createTriplanarPbrMaterial(
                this._scene,
                preset,
                tintColor,
                parallaxEnabled,
                tuning
            );
            this._advancedTextureMaterials[materialId] = material;
        }

        return materialId;
    }

    setAdvancedMaterialTunings(tunings: Record<string, IAdvancedMaterialTuning> = {}): void {
        const nextTunings: Record<string, IAdvancedMaterialTuning> = {};

        ADVANCED_MATERIAL_PRESETS.forEach(preset => {
            const tuning = getAdvancedMaterialTuning(preset, tunings[preset.id]);
            nextTunings[preset.id] = tuning;
            Object.entries(this._advancedTextureMaterials)
                .filter(([materialId]) => isAdvancedTextureMaterialForPreset(materialId, preset.id))
                .forEach(([, material]) =>
                    applyAdvancedMaterialTuningToMaterial(material, preset, tuning)
                );
        });

        this._advancedMaterialTunings = nextTunings;
    }

    getGLBMaterial(materialId: string, sourceId: string): BABYLON.StandardMaterial {
        if (!materialId) return this._defaultMaterial;
        if (this._materials[materialId + "_" + sourceId]) return this._materials[materialId + "_" + sourceId];
        if (this._materials[materialId]) return this._materials[materialId];
        return this._defaultMaterial;
    }

    getSelectionMaterialName(normalMaterialId: string): string {
        const normalMaterial = this.getMaterial(normalMaterialId);
        if (normalMaterialId.startsWith("XRayMaterial")) {
            return this.getOrCreateSelectionMaterial(1, normalMaterial.wireframe).name;
        }
        return this.getOrCreateSelectionMaterial(1, normalMaterial.wireframe).name;
    }

    getSelectionBackfaceMaterialName(normalMaterialId: string): string {
        const normalMaterial = this.getMaterial(normalMaterialId);
        if (normalMaterialId.startsWith("XRayMaterial")) {
            return this.getOrCreateSelectionMaterial(1, normalMaterial.wireframe, false).name;
        }
        return this.getOrCreateSelectionMaterial(1, normalMaterial.wireframe, false).name;
    }

    getHiddenMaterialName(_normalMaterialId: string): string {
        return this.getOrCreateHiddenMaterial().name;
    }

    changeMaterialProperties(materialId: string, diffuseColor: BABYLON.Color3, alpha: number) {
        const mat = this.getMaterial(materialId);
        if (mat.id == "XRayMaterial") return;

        if (mat.id != "DefaultMaterial" || (mat.id == "DefaultMaterial" && materialId == "DefaultMaterial")) {
            mat.alpha = alpha;
            mat.diffuseColor = diffuseColor;
            mat.specularColor = new BABYLON.Color3(0, 0, 0);
            mat.ambientColor = new BABYLON.Color3(0, 0, 0);
            mat.emissiveColor = new BABYLON.Color3(0, 0, 0);
        }
    }

    overrideSelectionMaterial(diffuseColor: BABYLON.Color3) {
        const isWireframe = this._displayMode == Enumerations.MaterialEdgeMode.WIREFRAME;
        const selMat = this.getOrCreateSelectionMaterial(1, isWireframe);
        selMat.diffuseColor = diffuseColor;
        selMat.ambientColor = diffuseColor;
        selMat.emissiveColor = new BABYLON.Color3(.2, .2, .2);
        this._isSelectionMaterialOverriden = true;
    }

    resetSelectionMaterial() {
        if (this._isSelectionMaterialOverriden) {
            const isWireframe = this._displayMode == Enumerations.MaterialEdgeMode.WIREFRAME;
            const selMat = this.getOrCreateSelectionMaterial(1, isWireframe);
            selMat.diffuseColor = MaterialManager.DEF_SELECTION_COLOR;
            selMat.ambientColor = MaterialManager.DEF_SELECTION_COLOR;
            selMat.emissiveColor = MaterialManager.DEF_SELECTION_EMISSIVE_COLOR;
            this._isSelectionMaterialOverriden = false;
        }
    }

    private getOrCreateAlphaMaterial(material: BABYLON.StandardMaterial, alpha: number, wireframe: boolean): BABYLON.StandardMaterial {
        let alphaMaterialName: string;
        let boxedAlpha: number;

        if (alpha == material.alpha) {
            alphaMaterialName = material.name;
            boxedAlpha = alpha;
        } else {
            let pcnt = "";
            if (alpha < 0.1) pcnt = "05";
            else if (alpha < 0.2) pcnt = "15";
            else if (alpha < 0.3) pcnt = "25";
            else if (alpha < 0.4) pcnt = "35";
            else if (alpha < 0.5) pcnt = "45";
            else if (alpha < 0.6) pcnt = "55";
            else if (alpha < 0.7) pcnt = "65";
            else if (alpha < 0.8) pcnt = "75";
            else if (alpha < 0.9) pcnt = "85";
            else if (alpha < 1.0) pcnt = "95";
            alphaMaterialName = material.name + pcnt;
            boxedAlpha = +pcnt / 100;
        }


        let alphaMat: BABYLON.StandardMaterial = this._materials[alphaMaterialName];
        if (!alphaMat) {
            alphaMat = new BABYLON.StandardMaterial(alphaMaterialName, this._scene);
            alphaMat.diffuseColor = material.diffuseColor;
            alphaMat.ambientColor = material.ambientColor;
            alphaMat.specularColor = material.specularColor;
            alphaMat.emissiveColor = material.emissiveColor;
            alphaMat.specularPower = material.specularPower;
            alphaMat.alpha = boxedAlpha;
            alphaMat.wireframe = wireframe;
            alphaMat.id = alphaMaterialName;

            this._materialKeys.push(alphaMat.id);
            this._materials[alphaMat.id] = alphaMat;
        }
        return alphaMat;
    }

    private getOrCreateSelectionMaterial(alpha: number, wireframe: boolean, backfaceCulling : boolean = true): BABYLON.StandardMaterial {
        let selectionMaterialName: string;
        let boxedAlpha: number;

        if (alpha < 1.0) {
            let pcnt: string;
            if (alpha < 0.1) pcnt = "05";
            else if (alpha < 0.2) pcnt = "15";
            else if (alpha < 0.3) pcnt = "25";
            else if (alpha < 0.4) pcnt = "35";
            else if (alpha < 0.5) pcnt = "45";
            else if (alpha < 0.6) pcnt = "55";
            else if (alpha < 0.7) pcnt = "65";
            else if (alpha < 0.8) pcnt = "75";
            else if (alpha < 0.9) pcnt = "85";
            else pcnt = "95";

            selectionMaterialName = MaterialManager.SELECTION_MATERIAL_TRANSPARENT + pcnt;
            boxedAlpha = +pcnt / 100;
        } else {
            selectionMaterialName = MaterialManager.SELECTION_MATERIAL_OPAQUE;
            boxedAlpha = 1.0;
        }

        let selectionMat: BABYLON.StandardMaterial = this._materials[selectionMaterialName];
        if (!selectionMat) {
            const defSelMat = (alpha < 1.0) ? this.getOrCreateSelectionMaterial(1, wireframe, backfaceCulling) : null;
            selectionMat = new BABYLON.StandardMaterial(selectionMaterialName, this._scene);
            selectionMat.diffuseColor = defSelMat?.diffuseColor ?? MaterialManager.DEF_SELECTION_COLOR;
            selectionMat.ambientColor = defSelMat?.ambientColor ?? MaterialManager.DEF_SELECTION_COLOR;
            selectionMat.specularColor = defSelMat?.specularColor ?? new BABYLON.Color3(.8, .8, .8);
            selectionMat.emissiveColor = defSelMat?.emissiveColor ?? MaterialManager.DEF_SELECTION_EMISSIVE_COLOR;
            selectionMat.specularPower = 64;
            selectionMat.alpha = boxedAlpha;
            selectionMat.wireframe = wireframe;
            selectionMat.id = selectionMaterialName;
            selectionMat.backFaceCulling = backfaceCulling;

            this._materialKeys.push(selectionMat.id);
            this._materials[selectionMat.id] = selectionMat;
        }
        return selectionMat;
    }

    private getOrCreateHiddenMaterial(): BABYLON.StandardMaterial {
        const hiddenMaterialName = MaterialManager.HIDDEN_MATERIAL;
        let hiddenMat: BABYLON.StandardMaterial = this._materials[hiddenMaterialName];

        if (!hiddenMat) {
            hiddenMat = new BABYLON.StandardMaterial(hiddenMaterialName, this._scene);
            hiddenMat.diffuseColor = new BABYLON.Color3(.0, .8, .8);
            hiddenMat.ambientColor = new BABYLON.Color3(.0, .8, .8);
            hiddenMat.specularColor = new BABYLON.Color3(.8, .8, .8);
            hiddenMat.emissiveColor = new BABYLON.Color3(.0, .2, .2);
            hiddenMat.specularPower = 64;
            hiddenMat.alpha = 0;
            hiddenMat.wireframe = false;
            hiddenMat.id = hiddenMaterialName;

            this._materialKeys.push(hiddenMat.id);
            this._materials[hiddenMaterialName] = hiddenMat;
        }
        return hiddenMat;
    }

    private getGlbScopedMaterialKey(materialKey: any): string {
        const materialName = String(materialKey ?? "");
        if (!materialName) {
            return materialName;
        }

        return materialName.indexOf("GLB|") === 0 ? materialName : "GLB|" + materialName;
    }

    processMaterialData(serializedMaterials: any[], glbID: any, rootUrl: string = ""): void {
        if (!serializedMaterials || serializedMaterials.length === 0) return;

        serializedMaterials.forEach(matJson => {
            if (!matJson) return;

            const scopedMaterialId = this.getGlbScopedMaterialKey(matJson.id);
            const scopedMaterialName = this.getGlbScopedMaterialKey(matJson.name || matJson.id);

            matJson.id = scopedMaterialId;
            matJson.name = scopedMaterialName;
            if (!matJson || !matJson.id) return;

            if (this._materials[matJson.id + "_" + glbID]) return;

            if (this._materials[matJson.id] != null) {
                if (this.areMaterialsEqualFast(matJson, this._materials[matJson.id])) return;

                const mat = BABYLON.Material.Parse(matJson, this._scene, rootUrl);
                if (mat) {
                    mat.id = scopedMaterialId;
                    mat.name = scopedMaterialName;
                    mat.backFaceCulling = false;
                    this._materials[mat.id + "_" + glbID] = mat as any;
                    this._materialKeys.push(mat.id + "_" + glbID);
                    return;
                }
            } else {
                const mat = BABYLON.Material.Parse(matJson, this._scene, rootUrl);
                if (mat) {
                    mat.id = scopedMaterialId;
                    mat.name = scopedMaterialName;
                    mat.backFaceCulling = false;
                    this._materials[mat.id] = mat as any;
                    this._materialKeys.push(mat.id);
                }
            }
        });
    }

    private areSerializedColorComponentsEqual(serializedColor: any, color: BABYLON.Color3): boolean {
        if (serializedColor == null || color == null) return false;

        if (Array.isArray(serializedColor) && serializedColor.length >= 3) {
            return serializedColor[0] === color.r
                && serializedColor[1] === color.g
                && serializedColor[2] === color.b;
        }

        if (serializedColor instanceof BABYLON.Color3) {
            return serializedColor.equals(color);
        }

        return false;
    }

    areMaterialsEqualFast(mat1: any, mat2: BABYLON.Material): boolean {
        if (!mat1 || !mat2) return false;
        if (mat2 instanceof BABYLON.PBRMaterial) {
            const serializedAlbedo = mat1.albedo ?? mat1.albedoColor;
            const serializedAmbient = mat1.ambient ?? mat1.ambientColor;
            const serializedEmissive = mat1.emissive ?? mat1.emissiveColor;
            const serializedReflectivity = mat1.reflectivity ?? mat1.reflectivityColor ?? mat1.specular ?? mat1.specularColor;

            return this.areSerializedColorComponentsEqual(serializedAlbedo, mat2.albedoColor)
                && (serializedAmbient == null || this.areSerializedColorComponentsEqual(serializedAmbient, mat2.ambientColor))
                && (serializedEmissive == null || this.areSerializedColorComponentsEqual(serializedEmissive, mat2.emissiveColor))
                && (serializedReflectivity == null || this.areSerializedColorComponentsEqual(serializedReflectivity, mat2.reflectivityColor))
                && (mat1.alpha == null || mat1.alpha === mat2.alpha)
                && (mat1.backFaceCulling == null || mat1.backFaceCulling === mat2.backFaceCulling)
                && (mat1.metallic == null || mat1.metallic === mat2.metallic)
                && (mat1.roughness == null || mat1.roughness === mat2.roughness)
                && (mat1.microSurface == null || mat1.microSurface === mat2.microSurface);
        }
        if (mat2 instanceof BABYLON.StandardMaterial) {
            const serializedDiffuse = mat1.diffuse ?? mat1.diffuseColor;
            const serializedAmbient = mat1.ambient ?? mat1.ambientColor;
            const serializedEmissive = mat1.emissive ?? mat1.emissiveColor;
            const serializedSpecular = mat1.specular ?? mat1.specularColor;

            return this.areSerializedColorComponentsEqual(serializedDiffuse, mat2.diffuseColor)
                && this.areSerializedColorComponentsEqual(serializedAmbient, mat2.ambientColor)
                && this.areSerializedColorComponentsEqual(serializedEmissive, mat2.emissiveColor)
                && this.areSerializedColorComponentsEqual(serializedSpecular, mat2.specularColor)
                && (mat1.alpha == null || mat1.alpha === mat2.alpha)
                && (mat1.backFaceCulling == null || mat1.backFaceCulling === mat2.backFaceCulling);
        }
        return false;
    }

    areMaterialsEqual(mat1: BABYLON.Material, mat2: BABYLON.Material): boolean {
        if (!mat1 || !mat2) return false;
        if (mat1.constructor !== mat2.constructor) return false;

        const propsToCompare = [
            "alpha", "backFaceCulling", "wireframe", "fogEnabled",
            "checkReadyOnEveryCall", "checkReadyOnlyOnce", "disableDepthWrite"
        ];
        for (const prop of propsToCompare) {
            if ((mat1 as any)[prop] !== (mat2 as any)[prop]) return false;
        }

        if (mat1 instanceof BABYLON.StandardMaterial && mat2 instanceof BABYLON.StandardMaterial) {
            const stdProps = [
                "diffuseColor", "specularColor", "emissiveColor", "ambientColor",
                "diffuseTexture", "bumpTexture", "opacityTexture", "reflectionTexture",
                "specularPower", "useAlphaFromDiffuseTexture"
            ];
            for (const prop of stdProps) {
                const val1 = (mat1 as any)[prop];
                const val2 = (mat2 as any)[prop];

                if (val1 instanceof BABYLON.Color3 && val2 instanceof BABYLON.Color3) {
                    if (!val1.equals(val2)) return false;
                } else if (val1 instanceof BABYLON.BaseTexture && val2 instanceof BABYLON.BaseTexture) {
                    if (val1.name !== val2.name || val1.getTextureMatrix().updateFlag !== val2.getTextureMatrix().updateFlag) {
                        return false;
                    }
                } else if (val1 !== val2) {
                    return false;
                }
            }
        }
        return true;
    }

    getMeasurementMaterial(): Interfaces.MaterialInterfaces.IAnnotationMaterial { return this._measurementMaterial; }
    getScanMaterial(): BABYLON.StandardMaterial { return this._scanMaterial; }
    getHiddenSelectionMaterialName(): string { return this._hiddenSelectionMaterial.name; }
    getHiddenSelectionMaterial(): BABYLON.StandardMaterial { return this._hiddenSelectionMaterial; }

    getMarkedPointMaterial(id: string, assetOverrideId?: string): Interfaces.MaterialInterfaces.IAnnotationMaterial {
        if (this._markedPointMaterials[id] != null) {
            const mpMaterial = this._markedPointMaterials[id];

            if (assetOverrideId == null) {
                return mpMaterial;
            } else {
                for (const key in this._materials) {
                    if (assetOverrideId.indexOf(this._materials[key].id) > -1) {
                        const assetMaterial = this._materials[key];
                        if (this._markedPointMaterials[id + key] == null) {
                            const mpOverrideMaterial = new MarkedPointMaterial();
                            mpOverrideMaterial.backgroundColor = mpMaterial.backgroundColor;
                            mpOverrideMaterial.captionFont = mpMaterial.captionFont;
                            mpOverrideMaterial.captionTextColor = mpMaterial.captionTextColor;
                            mpOverrideMaterial.id = id + key;
                            mpOverrideMaterial.markerAppeararnceId = mpMaterial.markerAppeararnceId;
                            mpOverrideMaterial.lineColor = assetMaterial.diffuseColor.toHexString();
                            mpOverrideMaterial.lineWidth = mpMaterial.lineWidth;
                            mpOverrideMaterial.tagShape = mpMaterial.tagShape;
                            mpOverrideMaterial.primShape = mpMaterial.primShape;
                            mpOverrideMaterial.markerSize = mpMaterial.markerSize;
                            mpOverrideMaterial.tagVisible = mpMaterial.tagVisible;

                            this._markedPointMaterials[id + key] = mpOverrideMaterial;
                        }
                        return this._markedPointMaterials[id + key];
                    }
                }
            }
        }
        return this._defaultMPMaterial;
    }

    addMarkedPointMaterial(material: Data.MaterialData.MarkedPointMaterial) {
        const mpMaterial = new MarkedPointMaterial();
        mpMaterial.backgroundColor = material.BackgroundColor;
        mpMaterial.captionFont = material.CaptionFont;
        mpMaterial.captionTextColor = material.CaptionTextColor;
        mpMaterial.id = material.Id;
        mpMaterial.markerAppeararnceId = material.MarkerObjectAppearanceId;
        mpMaterial.lineColor = material.LineColor;
        mpMaterial.lineWidth = material.LineWidth;
        mpMaterial.tagShape = material.TagShape;
        mpMaterial.primShape = material.MarkerShape;
        mpMaterial.markerSize = material.MarkerSize;
        mpMaterial.tagVisible = material.TagIsVisible;

        this._markedPointMaterials[mpMaterial.id] = mpMaterial;
    }

    addMaterial(material: Data.MaterialData.Material) {
        const mat = new BABYLON.StandardMaterial(material.Name, this._scene);
        mat.diffuseColor = new BABYLON.Color3(material.DiffuseR / 255, material.DiffuseG / 255, material.DiffuseB / 255);
        mat.ambientColor = new BABYLON.Color3(material.AmbientR / 255, material.AmbientG / 255, material.AmbientB / 255);
        mat.specularColor = new BABYLON.Color3(material.SpecularR / 255, material.SpecularG / 255, material.SpecularB / 255);
        mat.emissiveColor = new BABYLON.Color3(material.EmissiveR / 255, material.EmissiveG / 255, material.EmissiveB / 255);
        mat.alpha = 1 - material.Transparency;

        mat.specularPower = material.Shininess * 100;
        mat.id = material.Id;
        mat.wireframe = this._displayMode == Enumerations.MaterialEdgeMode.WIREFRAME;

        this._materialKeys.push(mat.id);
        this._materials[mat.id] = mat;
    }

    dispose() {
        for (const presetId in this._advancedTextureMaterials) {
            try { this._advancedTextureMaterials[presetId]?.dispose(false, true); } catch { }
        }
        this._advancedTextureMaterials = {};

        this._splatByMM.forEach((mat) => { try { mat.dispose(); } catch { } });
        this._splatByMM.clear();
        this._splatSet.clear();

        try { this._pointCloudShaderMaterialOrig?.dispose(); } catch { }

        for (let i = 0; i < this._materialKeys.length; i++) {
            try {
                this._materials[this._materialKeys[i]].dispose();
                this._materials[this._materialKeys[i]] = null as any;
            } catch { }
        }

        for (const key in this._markedPointMaterials) {
            delete this._markedPointMaterials[key];
        }
        this._markedPointMaterials = null as any;
        this._materialKeys = null as any;
        this._materials = null as any;
        this._scene = null as any;
    }

    // -------------------- Internals --------------------

    /** Register shader sources once. */
    private _registerPointShadersIfNeeded() {
        if (MaterialManager._shadersRegistered) return;

        // SPLAT (screen-space discs; perspective-scaled)
        if (!BABYLON.Effect.ShadersStore["pointVertexShader"]) {
            BABYLON.Effect.ShadersStore["pointVertexShader"] = `
                precision highp float;

                attribute vec3 position;
                attribute vec4 color;

                uniform mat4 world;                // NEW
                uniform mat4 worldViewProjection;
                uniform mat4 view;

                uniform float spacingWorld;
                uniform float focalPx;
                uniform float minPx;
                uniform float maxPx;

                uniform float nearDepth;
                uniform float farDepth;
                uniform float farPx;
                uniform float nearMinPx;
                uniform float coverageScale;

                varying vec4 vColor;
                varying vec3 vPositionW;           // NEW

                void main() {
                    vec4 worldPos = world * vec4(position, 1.0);   // NEW
                    vPositionW = worldPos.xyz;                      // NEW

                    vec4 viewPos = view * worldPos;
                    float depth = max(0.001, abs(viewPos.z));

                    // Perspective size ~ spacing * focalPx / depth, scaled by coverage
                    float px = coverageScale * spacingWorld * (focalPx / depth);
                    gl_PointSize = clamp(px, minPx, maxPx);

                    vColor = vec4(color.rgb, 1.0);
                    gl_Position = worldViewProjection * vec4(position, 1.0);
                }
            `;
        }

        if (!BABYLON.Effect.ShadersStore["pointFragmentShader"]) {
            BABYLON.Effect.ShadersStore["pointFragmentShader"] = `
        precision highp float;

        varying vec4 vColor;
        varying vec3 vPositionW;

        // Up to 6 clip planes
        uniform int uClipPlaneCount;
        uniform vec4 uClipPlane1;
        uniform vec4 uClipPlane2;
        uniform vec4 uClipPlane3;
        uniform vec4 uClipPlane4;
        uniform vec4 uClipPlane5;
        uniform vec4 uClipPlane6;

        uniform int uUseMeshOcclusion;
        uniform sampler2D meshDepthSampler;
        uniform vec2 screenSize;
        uniform float cameraNear;
        uniform float cameraFar;
        uniform float meshCullTol;

        float unpackDepth(vec4 rgba) {
            // R = MSB, A = LSB
            return dot(rgba, vec4(
                1.0,
                1.0 / 255.0,
                1.0 / (255.0 * 255.0),
                1.0 / (255.0 * 255.0 * 255.0)
            ));
        }

        bool clippedByPlane(vec4 plane, vec3 P) {
            return dot(plane.xyz, P) + plane.w > 0.0;
        }

        // Nonlinear depth [0..1] -> view-space Z (positive distance)
        float depth01ToViewZ(float depth01, float nearZ, float farZ) {
            float z = depth01 * 2.0 - 1.0;
            return (2.0 * nearZ * farZ) / (farZ + nearZ - z * (farZ - nearZ));
        }

        uniform int uDepthIsPacked;

        float readDepth01(vec2 uv) {
            return texture2D(meshDepthSampler, uv).r;
        }

        void main() {
            // circular footprint
            vec2 uvDisc = gl_PointCoord * 2.0 - 1.0;
            if (dot(uvDisc, uvDisc) > 1.0) discard;

            // clip planes
            if (uClipPlaneCount > 0 && clippedByPlane(uClipPlane1, vPositionW)) discard;
            if (uClipPlaneCount > 1 && clippedByPlane(uClipPlane2, vPositionW)) discard;
            if (uClipPlaneCount > 2 && clippedByPlane(uClipPlane3, vPositionW)) discard;
            if (uClipPlaneCount > 3 && clippedByPlane(uClipPlane4, vPositionW)) discard;
            if (uClipPlaneCount > 4 && clippedByPlane(uClipPlane5, vPositionW)) discard;
            if (uClipPlaneCount > 5 && clippedByPlane(uClipPlane6, vPositionW)) discard;

            // --- screen-space mesh proximity cull ---
            if (uUseMeshOcclusion == 1) {
                vec2 uv = (floor(gl_FragCoord.xy) + vec2(0.5)) / screenSize;
                float meshDepth01 = readDepth01(uv);

                // Only test if a mesh actually wrote depth here
                if (meshDepth01 < 0.999999) {
                    // Non linear depth
                    //float meshViewZ = depth01ToViewZ(meshDepth01, cameraNear, cameraFar);
                    //float ptViewZ   = depth01ToViewZ(gl_FragCoord.z, cameraNear, cameraFar);

                    //float dz = abs(ptViewZ - meshViewZ);

                    float meshViewZ = mix(cameraNear, cameraFar, meshDepth01);     // linear depth map
                    float ptViewZ   = depth01ToViewZ(gl_FragCoord.z, cameraNear, cameraFar); // gpu depth
                    float dz = abs(ptViewZ - meshViewZ);

                    // Depth quantization estimate (24-bit depth)
                    float depthStep01 = 1.0 / 16777215.0;

                    float z0 = meshViewZ;
                    float z1 = depth01ToViewZ(
                        min(meshDepth01 + depthStep01, 0.999999),
                        cameraNear,
                        cameraFar
                    );
                    float quantZ = abs(z1 - z0);

                    // Expand tolerance by a few LSBs (cap to avoid runaway)
                    //float extra = min(4.0 * quantZ, 0.25);

                    //float tol = meshCullTol + extra;
                    float tol = meshCullTol;

                    if (dz < tol) {
                        discard;   // don’t draw this point fragment
                    }

                    // Pinkify point instead for debugging
                    //if (dz < tol) {
                        //gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0);
                        //return;
                    //}
                }
            }

            // normal point color
            gl_FragColor = vec4(vColor.rgb, 1.0);
        }
    `;
        }


        // ORIGINAL (fixed-pixel size)
        if (!BABYLON.Effect.ShadersStore["pointOrigVertexShader"]) {
            BABYLON.Effect.ShadersStore["pointOrigVertexShader"] = `
                precision highp float;

                attribute vec3 position;
                attribute vec4 color;
                attribute vec3 normal;

                uniform mat4 worldViewProjection;
                uniform mat4 world;
                uniform float uPointSize;

                varying vec4 vColor;
                varying vec3 vNormal;
                varying vec3 vPositionW;   // NEW

                void main() {
                    vec4 worldPos = world * vec4(position, 1.0); // NEW
                    vPositionW = worldPos.xyz;                   // NEW

                    gl_Position = worldViewProjection * vec4(position, 1.0);
                    vColor = color;
                    vNormal = normalize(mat3(world) * normal);
                    gl_PointSize = uPointSize;
                }
            `;
        }

        if (!BABYLON.Effect.ShadersStore["pointOrigFragmentShader"]) {
            BABYLON.Effect.ShadersStore["pointOrigFragmentShader"] = `
                precision highp float;

                varying vec4 vColor;
                varying vec3 vNormal;
                varying vec3 vPositionW;  // NEW

                // Up to 6 clip planes
                uniform int uClipPlaneCount;
                uniform vec4 uClipPlane1;
                uniform vec4 uClipPlane2;
                uniform vec4 uClipPlane3;
                uniform vec4 uClipPlane4;
                uniform vec4 uClipPlane5;
                uniform vec4 uClipPlane6;

                bool clippedByPlane(vec4 plane, vec3 P) {
                    // Babylon convention: discard positive side
                    return dot(plane.xyz, P) + plane.w > 0.0;
                }

                void main() {
                    vec2 uv = gl_PointCoord * 2.0 - 1.0;
                    if (dot(uv, uv) > 1.0) discard;

                    if (uClipPlaneCount > 0 && clippedByPlane(uClipPlane1, vPositionW)) discard;
                    if (uClipPlaneCount > 1 && clippedByPlane(uClipPlane2, vPositionW)) discard;
                    if (uClipPlaneCount > 2 && clippedByPlane(uClipPlane3, vPositionW)) discard;
                    if (uClipPlaneCount > 3 && clippedByPlane(uClipPlane4, vPositionW)) discard;
                    if (uClipPlaneCount > 4 && clippedByPlane(uClipPlane5, vPositionW)) discard;
                    if (uClipPlaneCount > 5 && clippedByPlane(uClipPlane6, vPositionW)) discard;

                    // simple lighting (unchanged)
                    vec3 lightDir = normalize(vec3(0.0, 1.0, 0.0));
                    float diffuse = max(dot(normalize(vNormal), lightDir), 0.0);
                    float ambient = 1.0;
                    float brightness = diffuse + ambient;

                    gl_FragColor = vec4(vColor.rgb * brightness, vColor.a);
                }
            `;
        }

        MaterialManager._shadersRegistered = true;
    }

    private _clampCoverage(x: number): number {
        return Math.min(Math.max(x, 0.6), 2.5);
    }

    /** Bind scene clip planes into a ShaderMaterial (SPLAT or ORIG). */
    private _bindClipPlanesTo(mat: BABYLON.ShaderMaterial) {
        const s = this._scene;
        const planes = [
            s.clipPlane, s.clipPlane2, s.clipPlane3,
            s.clipPlane4, s.clipPlane5, s.clipPlane6
        ].filter((p): p is BABYLON.Plane => !!p);

        mat.setInt("uClipPlaneCount", planes.length);

        const asVec4 = (p?: BABYLON.Plane) =>
            p ? new BABYLON.Vector4(p.normal.x, p.normal.y, p.normal.z, p.d)
                : new BABYLON.Vector4(0, 0, 0, 0);

        mat.setVector4("uClipPlane1", asVec4(planes[0]));
        mat.setVector4("uClipPlane2", asVec4(planes[1]));
        mat.setVector4("uClipPlane3", asVec4(planes[2]));
        mat.setVector4("uClipPlane4", asVec4(planes[3]));
        mat.setVector4("uClipPlane5", asVec4(planes[4]));
        mat.setVector4("uClipPlane6", asVec4(planes[5]));
    }

    /**
     * Create an isolated SPLAT ShaderMaterial configured for a specific spacing.
     * @param spacingMeters Voxel spacing in world meters (e.g. 0.005 for 5mm)
     */
    private _createSplatMaterial(spacingMeters: number): BABYLON.ShaderMaterial {
        const mm = Math.round(spacingMeters * 1000);

        const mat = new BABYLON.ShaderMaterial(
            `pointCloudShader_splat_${mm}mm`,
            this._scene,
            { vertex: "point", fragment: "point" },
            {
                attributes: ["position", "color"],
                uniforms: [
                    "world", "worldViewProjection", "view",
                    "spacingWorld", "focalPx",
                    "minPx", "maxPx",
                    "nearDepth", "farDepth", "farPx",
                    "nearMinPx", "coverageScale",

                    // clip uniforms
                    "uClipPlaneCount",
                    "uClipPlane1", "uClipPlane2", "uClipPlane3",
                    "uClipPlane4", "uClipPlane5", "uClipPlane6",

                    // NEW occlusion uniforms
                    "uUseMeshOcclusion",
                    "screenSize",
                    "cameraNear",
                    "cameraFar",
                    "uDepthIsPacked",
                    "meshCullTol"
                ],
                samplers: ["meshDepthSampler"]
            } as any
        );

        mat.onBindObservable.add(() => this._bindClipPlanesTo(mat));
        mat.pointsCloud = true;
        mat.backFaceCulling = false;
        mat.alphaMode = BABYLON.Constants.ALPHA_DISABLE;
        mat.allowShaderHotSwapping = true;

        // Defaults (your existing)
        mat.setFloat("nearDepth", 2.0);
        mat.setFloat("farDepth", 30.0);

        mat.setFloat("minPx", this._pcDynBase.minPx * this._pcDynScale);
        mat.setFloat("maxPx", this._pcDynBase.maxPx * this._pcDynScale);
        mat.setFloat("farPx", this._pcDynBase.farPx * this._pcDynScale);
        mat.setFloat("nearMinPx", this._pcNearMinPx);

        const base = this.getCoverageScaleForVoxel(mm);
        mat.setFloat("coverageScale", this._clampCoverage(base + this._covNudge));

        mat.setFloat("spacingWorld", spacingMeters);

        // NEW occlusion defaults
        mat.setInt("uUseMeshOcclusion", this._pcMeshOcclusionEnabled ? 1 : 0);
        mat.setInt("uDepthIsPacked", this._pcDepthIsPacked ? 1 : 0);
        mat.setFloat("meshCullTol", this._pcMeshOcclusionTolM);

        const cam = this._scene.activeCamera as BABYLON.Camera;
        const eng = this._scene.getEngine();
        mat.setVector2("screenSize", new BABYLON.Vector2(eng.getRenderWidth(true), eng.getRenderHeight(true)));
        mat.setFloat("cameraNear", cam?.minZ ?? 0.1);
        mat.setFloat("cameraFar", cam?.maxZ ?? 1000.0);

        if (this._pcMeshOcclusionEnabled) {
            this._ensureDepthResources();
            this._updateMeshDepthRenderList();
            if (this._meshDepthTex) {
                mat.setTexture("meshDepthSampler", this._meshDepthTex);
            }
        }

        return mat;
    }

}

class MarkedPointMaterial implements Interfaces.MaterialInterfaces.IAnnotationMaterial {
    id: string;
    markerAppeararnceId: string;
    lineWidth: number;
    lineColor: string;
    captionTextColor: string;
    captionFont: Data.MaterialData.Font;
    backgroundColor: string;
    tagShape: Enumerations.AnnotationLabelShape;
    primShape: Enumerations.MarkedPointPrimitiveShape;
    markerSize: number;
    tagVisible: boolean;
}

export default MaterialManager;
